import { useEffect, useState } from 'react';
import { Clock, Moon } from 'lucide-react';

interface Prayer {
  name: string;
  time: string;
  key?: string;
}

interface TimeAndPrayerDisplayProps {
  prayers: Prayer[];
}

export function TimeAndPrayerDisplay({ prayers }: TimeAndPrayerDisplayProps) {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [nextPrayer, setNextPrayer] = useState<{ name: string; time: string; timeLeft: string } | null>(null);

  const parseTimeToMinutes = (timeStr: string, prayerKey?: string): number => {
    if (!timeStr) return 0;

    const [hoursStr, minutesStr] = timeStr.split(':');
    let hours = parseInt(hoursStr);
    const minutes = parseInt(minutesStr);

    if (prayerKey) {
      const key = prayerKey.toLowerCase();
      if (key === 'fajr' || key === 'sunrise') {
        if (hours === 12) hours = 0;
      } else if (key === 'zuhr' || key === 'dhuhr') {
        if (hours !== 12) hours += 12;
      } else if (key === 'asr' || key === 'maghrib' || key === 'isha') {
        if (hours !== 12) hours += 12;
      }
    }

    return hours * 60 + minutes;
  };

  useEffect(() => {
    const updateTimeAndPrayer = () => {
      const now = new Date();
      setCurrentTime(now);

      const currentMinutes = now.getHours() * 60 + now.getMinutes();

      let nextPrayerInfo = null;
      let minDiff = Infinity;

      // First, try to find a prayer that hasn't happened yet today
      for (const prayer of prayers) {
        if (prayer.name === 'Sunrise') continue;
        const prayerMinutes = parseTimeToMinutes(prayer.time, prayer.key);
        const diff = prayerMinutes - currentMinutes;

        if (diff > 0 && diff < minDiff) {
          minDiff = diff;
          const hoursLeft = Math.floor(diff / 60);
          const minutesLeft = diff % 60;

          let timeLeftStr = '';
          if (hoursLeft > 0) {
            timeLeftStr = `${hoursLeft}h ${minutesLeft}m`;
          } else {
            timeLeftStr = `${minutesLeft}m`;
          }

          nextPrayerInfo = {
            name: prayer.name,
            time: prayer.time,
            timeLeft: timeLeftStr
          };
        }
      }

      // If no prayer found today, find the first prayer of tomorrow
      if (!nextPrayerInfo) {
        for (const prayer of prayers) {
          if (prayer.name === 'Sunrise') continue;
          const prayerMinutes = parseTimeToMinutes(prayer.time, prayer.key);
          const diff = prayerMinutes - currentMinutes + 24 * 60;

          if (diff < minDiff) {
            minDiff = diff;
            const hoursLeft = Math.floor(diff / 60);
            const minutesLeft = diff % 60;

            let timeLeftStr = '';
            if (hoursLeft > 0) {
              timeLeftStr = `${hoursLeft}h ${minutesLeft}m`;
            } else {
              timeLeftStr = `${minutesLeft}m`;
            }

            nextPrayerInfo = {
              name: prayer.name,
              time: prayer.time,
              timeLeft: timeLeftStr
            };
          }
        }
      }

      setNextPrayer(nextPrayerInfo);
    };

    updateTimeAndPrayer();
    const interval = setInterval(updateTimeAndPrayer, 1000);

    return () => clearInterval(interval);
  }, [prayers]);

  return (
    <div className="fixed top-0 left-0 right-0 sm:top-4 sm:left-4 sm:right-4 z-10">
      <div className="max-w-7xl mx-auto">
        {/* Compact header layout */}
        <div className="bg-gradient-to-br from-teal-700/30 to-emerald-700/30 backdrop-blur-md rounded-none sm:rounded-2xl shadow-2xl border-2 border-teal-400/50 px-4 py-4 sm:px-4 sm:py-4 md:px-6 md:py-5">
          <div className="flex flex-col items-center gap-2 sm:gap-3">
            {/* Big Clock */}
            <div className="text-center w-full">
              <div
                className="font-black text-white drop-shadow-2xl tracking-wider leading-none"
                style={{ fontSize: 'clamp(5rem, 18vw, 8rem)' }}
              >
                {currentTime.toLocaleTimeString('en-US', {
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                  hour12: true
                }).replace(/\s?(AM|PM|am|pm)/g, '')}
              </div>
              <div className="text-base sm:text-lg md:text-xl lg:text-2xl text-white/90 mt-1 sm:mt-2 font-semibold">
                {currentTime.toLocaleDateString('en-US', {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric'
                })}
              </div>
            </div>

            {/* Next Prayer - Below clock */}
            {nextPrayer && (
              <div className="mt-1 sm:mt-2 pt-2 sm:pt-3 border-t-2 border-white/20 w-full">
                <div className="flex items-center justify-center gap-2 sm:gap-3 flex-wrap">
                  <Moon className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 lg:w-8 lg:h-8 flex-shrink-0 text-white" />
                  <div
                    className="flex items-baseline gap-2 sm:gap-3 md:gap-4 flex-wrap justify-center font-bold text-white"
                    style={{ fontSize: 'clamp(1rem, 4vw, 2.5rem)' }}
                  >
                    <span>{nextPrayer.name}</span>
                    <span className="font-semibold">at {nextPrayer.time}</span>
                    <span
                      className="bg-white/20 rounded-lg px-2 py-1 sm:px-3 sm:py-1.5"
                      style={{ fontSize: 'clamp(0.875rem, 3.5vw, 2rem)' }}
                    >
                      in {nextPrayer.timeLeft}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
